import React, { ReactNode, useState, useEffect, useMemo } from 'react';
import { Box, Menu, MenuButton, MenuList, useMediaQuery } from '@chakra-ui/react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTenantIdContext } from '../../../context/TenantIdProvider';
import IconComponent from '../../IconComponent/IconComponent';
import translateRole from '../../../utils/translateRole';
import useAuth from '../../../hooks/useAuth';

import './WideHeader.css';
import { useHideTenantSelector } from './use-hide-tenant-selector';
import { SearchableSelect } from '../SelectWithSearch';

interface Props {
  dropdownMenuList: ReactNode;
  showLogo?: boolean;
  unreadMessageCount?: number;
}

const WideHeader = ({ dropdownMenuList, showLogo = true, unreadMessageCount }: Props) => {
  const { auth, logout } = useAuth();
  const { tenantId, setTenantId } = useTenantIdContext();
  const { admin, childOwner, tenantAdmin, superAdmin } = auth.loggedUserDto;
  const { id: tenant } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const hideTenantSelector = useHideTenantSelector();

  const [isLessThanOrEqual1024] = useMediaQuery('(max-width: 1024px)');
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    if (tenant === 'undefined' && auth.availableTenants.length > 0) {
      const defaultTenantId = auth.availableTenants[0].id;
      setTenantId(defaultTenantId.toString());
      const updatedUrl = window.location.pathname.replace(
        '/id/undefined',
        `/id/${defaultTenantId}`
      );
      navigate(updatedUrl);
    } else {
      setTenantId(tenant);
    }
  }, [tenant, auth.availableTenants, navigate, setTenantId]);

  const handleTenantChange = (newId: number) => {
    const currentUrl = window.location.pathname;
    const regex = /\/id\/\d+/;

    let updatedUrl;
    if (regex.test(currentUrl)) {
      updatedUrl = currentUrl.replace(regex, `/id/${newId}`);
    } else {
      updatedUrl = currentUrl.replace('/id/undefined', `/id/${newId}`);
    }

    navigate(updatedUrl);
    setTenantId(String(newId));
  };

  const tenantToSelect = useMemo(
    () =>
      auth.availableTenants.map((tenant) => ({
        label: `${tenant.name} (${tenant.id})`,
        value: tenant.id
      })),
    [auth.availableTenants]
  );

  const userInfo = (userCircle: boolean) => (
    <div className="Header_userContainer p-spacing-xl pr-8 flex items-center">
      {userCircle && (
        <div className="Header_userIcon w-10 h-10 flex justify-center items-center cursor-pointer p-spacing-md">
          <IconComponent iconName="userCircle" />
        </div>
      )}
      <div className="Header_userInfo">
        <p className="Header_userInfo__name text-base font-semibold">
          {auth.loggedUserDto.firstName}
        </p>
        <p className="Header__userInfo__role font-normal">
          {translateRole({
            admin,
            childOwner,
            tenantAdmin,
            superAdmin
          })}
        </p>
      </div>
      {showMenu && (
        <div
          className="flex flex-1 justify-end items-center"
          onClick={() => {
            setShowMenu(false);
          }}>
          <IconComponent iconName="menu" className="mr-spacing-xl" />
        </div>
      )}
    </div>
  );

  return (
    <header className="Header flex flex-row pr-8 pl-4">
      {showMenu && (
        <div className="w-screen h-screen fixed top-0 left-0 bg-white z-50">
          <div className="flex flex-row pr-8 pl-4">
            {showLogo === true && (
              <div className="Header__selectContainer">
                <Link to="/">
                  <IconComponent iconName="logo" className="Header__logoContainer mr-spacing-xl" />
                </Link>
              </div>
            )}
          </div>
          <div className="ml-2">
            {userInfo(false)}
            <div className="grid grid-rows-3 gap-spacing-xl pt-spacing-3xl pb-spacing-3xl border-t border-b border-grayLight-200">
              <Link
                to="#"
                className="flex items-center pr-spacing-3xl pl-spacing-3xl font-semibold text-base">
                <IconComponent iconName="message" className="mr-spacing-xl" />
                Wiadomości
                {unreadMessageCount && unreadMessageCount > 0 && (
                  <span className="ml-2 mb-1.5 text-xs bg-brand-600 text-white rounded-full px-1.5 py-0.5">
                    {unreadMessageCount}
                  </span>
                )}
              </Link>
              <Link
                to="#"
                className="flex items-center pr-spacing-3xl pl-spacing-3xl font-semibold text-base">
                <IconComponent iconName="settings" className="mr-spacing-xl" />
                Ustawienia
              </Link>
            </div>
            <button
              type="button"
              onClick={logout}
              className="flex items-center pr-spacing-3xl pl-spacing-3xl font-semibold text-base bg-none border-none text-error-700 mt-spacing-md">
              {' '}
              Wyloguj
              <IconComponent iconName="logOutDoor" className="ml-spacing-xl" />
            </button>
          </div>
        </div>
      )}
      <div className="Header__selectContainer flex justify-start items-center">
        {showLogo && (
          <Link to="/">
            <IconComponent iconName="logo" className="Header__logoContainer" />
          </Link>
        )}
        <div className="h-20" />
        {!hideTenantSelector && (
          <SearchableSelect
            value={tenantId}
            onChange={handleTenantChange}
            options={tenantToSelect}
          />
        )}
      </div>
      {isLessThanOrEqual1024 && (
        <div
          className="flex flex-1 justify-end items-center"
          onClick={() => {
            setShowMenu(true);
          }}>
          <IconComponent iconName="menu" className="mr-spacing-xl" />
        </div>
      )}
      <div className="Header__actionContainer flex-1 justify-end hidden lg:flex">
        <div className="Header_userAction flex justify-center">
          <Menu>
            <MenuButton as={Box} aria-label="Options" className="cursor-pointer flex items-center">
              {userInfo(true)}
            </MenuButton>
            <MenuList zIndex="dropdown" boxShadow="lg">
              {dropdownMenuList}
            </MenuList>
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default WideHeader;
